import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { logger } from '@/utils/logger/logger';

import { CommandItemWrapper } from './single-select-option-renderers';
import { SingleSelectOption } from './single-select-types';

const TAG = 'SingleSelectCommandList:';

interface CommandListComponentProps {
  options: SingleSelectOption[];
  selectedValue: string;
  onSelectOption: (value: string) => void;
  renderOption?: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
  defaultRenderOption: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
  renderGroupHeading?: (groupKey: string) => React.ReactNode;
  searchable: boolean;
  hideSearchWhenEmpty: boolean;
  searchValue: string;
  searchPlaceholder: string;
  onSearchChange: (value: string) => void;
  maxHeight: string;
  emptyMessage: string;
  noOptionsMessage?: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  setContainerElement: (element: HTMLElement | null) => void;
  setSentinelElement: (element: HTMLElement | null) => void;
  isCreatable?: boolean;
  onCreateOption?: (inputValue: string) => SingleSelectOption | Promise<SingleSelectOption>;
}

interface OptionGroup {
  key: string | undefined;
  options: SingleSelectOption[];
}

/**
 * Partitions options into consecutive runs sharing the same `groupKey`.
 * Options are expected to already arrive pre-sorted by group — this only
 * splits the existing order into sections, it never re-sorts.
 */
const partitionByGroupKey = (options: SingleSelectOption[]): OptionGroup[] => {
  const groups: OptionGroup[] = [];

  for (const option of options) {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.key === option.groupKey) {
      lastGroup.options.push(option);
    } else {
      groups.push({ key: option.groupKey, options: [option] });
    }
  }

  return groups;
};

export function SingleSelectCommandListComponent({
  options,
  selectedValue,
  onSelectOption,
  renderOption,
  defaultRenderOption,
  renderGroupHeading,
  searchable,
  hideSearchWhenEmpty,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  maxHeight,
  emptyMessage,
  noOptionsMessage,
  hasNextPage,
  isFetchingNextPage,
  setContainerElement,
  setSentinelElement,
  isCreatable = false,
  onCreateOption,
}: CommandListComponentProps) {
  const { t } = useTranslation(['common']);

  // CommandList component intercepts the scroll event
  // in favor of keyboard navigation
  // so we stop propagation
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <Command shouldFilter={false}>
      {searchable && !(hideSearchWhenEmpty && options.length === 0) && (
        <CommandInput
          placeholder={searchPlaceholder}
          value={searchValue}
          onValueChange={onSearchChange}
          className="border-0 focus:ring-0"
        />
      )}
      <CommandList className="max-h-[300px]" style={{ maxHeight }}>
        <div
          ref={setContainerElement}
          className="overflow-y-auto"
          style={{ maxHeight }}
          onWheel={handleWheel}
        >
          {options.length === 0 && !(isCreatable && searchValue) ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {noOptionsMessage || t('common:noOptionsAvailable')}
            </div>
          ) : (
            (() => {
              const groups = partitionByGroupKey(options);
              // A single group (or no renderGroupHeading) renders as a flat list — headings
              // only make sense once options are actually split across sections.
              const showHeadings = !!renderGroupHeading && groups.length > 1;

              return groups.map((group, groupIndex) => {
                const isLastGroup = groupIndex === groups.length - 1;
                return (
                  <CommandGroup
                    key={group.key ?? `ungrouped-${groupIndex}`}
                    heading={showHeadings && group.key ? renderGroupHeading!(group.key) : undefined}
                  >
                    {group.options.map((option, index) => (
                      <CommandItemWrapper
                        key={`${option.value}-${index}`}
                        option={option}
                        isSelected={selectedValue === option.value}
                        onSelect={onSelectOption}
                        renderOption={renderOption}
                        defaultRenderOption={defaultRenderOption}
                      />
                    ))}
                    {isLastGroup && isFetchingNextPage && (
                      <div className="py-2 text-center text-xs text-muted-foreground">
                        {t('loadingMore')}
                      </div>
                    )}
                    {isLastGroup && hasNextPage && <div ref={setSentinelElement} className="h-1" />}
                  </CommandGroup>
                );
              });
            })()
          )}
          {isCreatable &&
            searchValue &&
            !options.some(option => option.label.toLowerCase() === searchValue.toLowerCase()) && (
              <CommandGroup className="pt-0">
                <CommandItem
                  onSelect={() => {
                    if (onCreateOption) {
                      const result = onCreateOption(searchValue);
                      const handle = (newOption: SingleSelectOption) => {
                        onSelectOption(newOption.value);
                        onSearchChange('');
                      };
                      if (result instanceof Promise) {
                        result.then(handle).catch((error: unknown) => {
                          logger.error(TAG, 'Failed to create option:', error);
                        });
                      } else {
                        handle(result);
                      }
                    }
                  }}
                  className="cursor-pointer"
                >
                  <CirclePlus className="mr-2 h-4 w-4" />
                  <span>{`${t('add')} "${searchValue}"`}</span>
                </CommandItem>
              </CommandGroup>
            )}
        </div>
      </CommandList>
    </Command>
  );
}
